"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@/module_bindings";
import { tsToIso, type MyMembershipRow } from "@/lib/stdb-map";

/**
 * Raw `my_memberships` row, lightly normalized (timestamps -> ISO strings).
 * No user-join is needed here — every row IS one of the caller's own
 * memberships, so there's nothing to look up.
 */
export interface MemberRowLite {
  id: string;
  userId: string;
  organizationId: string;
  inviterId: string;
  inviteToken: string;
  isPowerOnProPlus: boolean;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

function mapMembershipRow(row: MyMembershipRow): MemberRowLite {
  return {
    id: row.id,
    userId: row.userId,
    organizationId: row.organizationId,
    inviterId: row.inviterId,
    inviteToken: row.inviteToken,
    isPowerOnProPlus: row.isPowerOnProPlus,
    role: row.role,
    status: row.status,
    createdAt: tsToIso(row.createdAt),
    updatedAt: tsToIso(row.updatedAt),
  };
}

export function useMyMemberships(): {
  memberships: MemberRowLite[];
  isReady: boolean;
} {
  const [rows, isReady] = useTable(tables.my_memberships);
  const memberships = useMemo(() => rows.map(mapMembershipRow), [rows]);
  return { memberships, isReady };
}

export interface PendingOrg {
  id: string;
  name: string;
  logo: string;
}

export function usePendingOrgs(): {
  pendingOrgs: PendingOrg[];
  isReady: boolean;
} {
  const [rows, isReady] = useTable(tables.my_pending_orgs);
  const pendingOrgs = useMemo(
    () => rows.map((row) => ({ id: row.id, name: row.name, logo: row.logo })),
    [rows],
  );
  return { pendingOrgs, isReady };
}
